import type { ComponentProps } from 'react';
import React, { lazy, Suspense } from 'react';
import styled from '@emotion/styled';
import { ProductEventDataList } from 'corgi-types';
import type {
  BorderRadiusProps,
  BoxShadowProps,
  DisplayProps,
  FlexDirectionProps,
  HeightProps,
  JustifyContentProps,
  MaxWidthProps,
  MinWidthProps,
  SpaceProps,
  WidthProps,
} from 'styled-system';
import {
  backgroundColor,
  borderRadius,
  boxShadow,
  display,
  flexDirection,
  height,
  justifyContent,
  marginBottom,
  maxWidth,
  minWidth,
  overflow,
  space,
  width,
} from 'styled-system';

import type { Prices } from 'common/components/ArticlePreview/types';
import Carousel from 'common/components/Carousel';
import ContainerWithBackground from 'common/components/ContainerWithBackground';
import ProductCard from 'common/components/ProductCard';
import { QuantitySelector } from 'common/components/QuantitySelector';
import { GrowthbookFlag } from 'common/constants/feature-flags';
import { AddToCartPlacement } from 'common/enums/Tracking';
import { useFeatureFlagIsOn } from 'common/hooks/feature-flags';
import { useLocalization } from 'common/providers/LocaleProvider';
import theme from 'common/theme/theme';
import type { AccessorySliderProps } from 'common/types/accessory-slider';
import { SLIDE_WIDTH, SMALL_SLIDE_WIDTH } from './constants';
import { useAccessories, useAccessoryCart } from './hooks';
import { useSlideSize } from './hooks/slide';
import { accessoryToProduct } from './utils';

const AddToCart = lazy(() => import('../../add-to-cart/components/AddToCart'));

const SLIDE_CELL_SPACING = 8;

const UnstyledContainer = styled(ContainerWithBackground)<any>`
  ${space};
  ${backgroundColor};
  ${borderRadius};
  ${overflow};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer pb="sp_48" overflow="hidden" {...props} />
);

const SlideContainer = styled.section<
  MinWidthProps &
    MaxWidthProps &
    DisplayProps &
    FlexDirectionProps &
    JustifyContentProps &
    WidthProps &
    BoxShadowProps &
    BorderRadiusProps
>`
  ${width};
  ${maxWidth};
  ${display};
  ${flexDirection};
  ${justifyContent};
  ${minWidth};
  ${boxShadow};
  ${borderRadius};

  &:hover,
  &:active {
    box-shadow: ${theme.shadows.low};
  }
`;

const StyledAddToCart = styled(AddToCart)<
  ComponentProps<typeof AddToCart> &
    DisplayProps &
    JustifyContentProps &
    SpaceProps &
    WidthProps &
    MinWidthProps
>`
  ${display};
  ${justifyContent};
  ${space};
  ${width};
  ${minWidth};

  button {
    height: 32px;
    padding-left: ${theme.space.sp_8}px;
    padding-right: ${theme.space.sp_8}px;
  }
`;

const addToCartStyles: Partial<ComponentProps<typeof StyledAddToCart>> = {
  display: 'flex',
  justifyContent: 'center',
  my: 'sp_8',
  px: 'sp_8',
  minWidth: '138px',
};

const StyledQuantitySelector = styled(QuantitySelector)<
  ComponentProps<typeof QuantitySelector> &
    DisplayProps &
    JustifyContentProps &
    SpaceProps &
    WidthProps &
    HeightProps
>`
  ${display};
  ${justifyContent};
  ${space};
  ${width};
  ${height};
  ${marginBottom};
`;

const quantitySelectorStyles: Partial<ComponentProps<typeof StyledQuantitySelector>> = {
  width: '85%',
  margin: '10px auto 0',
  height: 'sp_32',
  justifyContent: 'space-between',
  padding: 'sp_0',
  marginBottom: ['sp_0', 'sp_0', 'sp_10', '15px'],
};

// eslint-disable-next-line max-lines-per-function, complexity
const AccessorySlider = ({
  accessories,
  slideWidth,
  title = 'h24_accessory_slider_headline',
  ctaButtonKind = 'primary',
}: AccessorySliderProps) => {
  const { t } = useLocalization();
  const { slidesToShow } = useSlideSize();
  const { sellableItems, handleAfterSlide } = useAccessories({
    accessories,
    slidesToShow,
  });
  const { cartItems, handleAddToCart, handleQuantityChange, handleRemove } = useAccessoryCart();
  const isSmallVariant = useFeatureFlagIsOn(GrowthbookFlag.AccessorySliderSmaller);
  const slideWidthValue = isSmallVariant ? SMALL_SLIDE_WIDTH : (slideWidth ?? SLIDE_WIDTH);

  if (!sellableItems.length) {
    return null;
  }

  return (
    <Container
      data-corgi-id="accessory-slider"
      data-testid="accessory-slider"
      px={['sp_16', null, null, 'sp_24']}
      py={['sp_16', null, null, 'sp_20']}
    >
      <Carousel
        title={t(title)}
        afterSlide={handleAfterSlide}
        slideGap={SLIDE_CELL_SPACING}
        withTopControls
        withBoxShadow
      >
        {sellableItems.map((accessory, index) => {
          const cartItem = cartItems.get(accessory.sku);
          const isInCart = cartItem && cartItem.quantity > 0;

          return (
            <SlideContainer
              key={accessory.sku}
              data-testid="accessory-slide-container"
              minWidth={slideWidthValue}
              width={slideWidthValue}
              maxWidth={234}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              boxShadow="extraLow"
              borderRadius="medium"
            >
              <ProductCard
                article={accessoryToProduct(accessory)}
                index={index}
                productEventDataListType={ProductEventDataList.Accessories}
                withProminentPricing
                isSmallVariant={isSmallVariant}
              />
              {isInCart ? (
                <StyledQuantitySelector
                  {...quantitySelectorStyles}
                  productName={accessory.name}
                  maxQuantity={accessory.stock?.quantity ?? 10}
                  initialQuantity={cartItem.quantity}
                  onQuantitySelect={quantity => handleQuantityChange(accessory.sku, quantity)}
                  onRemove={() => handleRemove(accessory.sku)}
                  withRemoveOption={false}
                />
              ) : (
                <Suspense fallback={null}>
                  <StyledAddToCart
                    sku={accessory.sku}
                    name={accessory.name}
                    variantName={accessory.variantName}
                    images={accessory.images}
                    prices={accessory.prices as Prices}
                    delivery={accessory.delivery}
                    sellable={accessory.sellable}
                    stock={accessory.stock}
                    bestOffer={accessory.bestOffer}
                    tracking={{
                      ...accessory.tracking,
                      placement: AddToCartPlacement.AccessorySlider,
                    }}
                    isMinified
                    hideQuantity
                    ctaLabel="h24_accessory_add"
                    isRounded
                    noBottomMargin
                    withIcon
                    disableDrawerOpening
                    onAddToCart={cart => handleAddToCart(accessory.sku, cart)}
                    buttonKind={ctaButtonKind}
                    {...addToCartStyles}
                  />
                </Suspense>
              )}
            </SlideContainer>
          );
        })}
      </Carousel>
    </Container>
  );
};

export default AccessorySlider;
